import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import { LockOutlined as LockOutlinedIcon } from '@mui/icons-material';
import { useProductosMaterialContext } from 'context';
import { useApi } from 'lib/api';
import {
  PROGRAMA_REGISTRO_EMPLEADOS,
  puedeVerProgramaNominaPropio,
} from 'lib/seguridad/permisosPorModulo';
import { FP, PageHeader, fpDataGridSx, fpInputSx } from 'components/financepro';

const btnBase = { textTransform: 'none', borderRadius: 1.8, fontWeight: 700, boxShadow: 'none' };
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: '#FFFFFF',
  border: `1px solid ${c}`,
  '&:hover': { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
  '&.Mui-disabled': { color: FP.color.textMuted, borderColor: FP.color.border },
});
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: '#FFFFFF',
  '&:hover': { bgcolor: hover, boxShadow: 'none', color: '#FFFFFF' },
  '&.Mui-disabled': { bgcolor: `${bg}66`, color: '#FFFFFF' },
});

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 0,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

const cardShellSx = {
  borderRadius: 2.5,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
  border: `1px solid ${FP.color.border}`,
  overflow: 'hidden',
};

const tabSx = {
  minHeight: 40,
  bgcolor: 'transparent',
  p: 0,
  borderRadius: 0,
  '& .MuiTab-root': {
    textTransform: 'none',
    fontWeight: 700,
    minHeight: 40,
    color: FP.color.textSecondary,
    opacity: 1,
  },
  '& .MuiTab-root.Mui-selected': { color: FP.color.navy },
  '& .MuiTabs-indicator': {
    backgroundColor: FP.color.navy,
    height: 3,
    borderRadius: '3px 3px 0 0',
    boxShadow: 'none',
  },
};

const SEXO = ['Femenino', 'Masculino'];
const ESTADO_CIVIL = ['Casado', 'Soltero'];
const DEPENDIENTES = ['0', '1', '2', '3', '4', '5', '6', '7'];
const NACIONALIDAD = ['Dominicano', 'Extranjero'];
const SANGRE = ['A+', 'A-', 'O+', 'O-', 'AB+', 'AB-'];
const CLASE = ['Fijo', 'Produccion', 'Horas'];
const PAGO = ['Semanal', 'Quincenal', 'Mensual'];
const SI_NO = ['Si', 'No'];
const ESTATUS = ['Activo', 'Cancelado'];

function hoyISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`;
}

function formularioVacio(ultimo) {
  const siguiente = Number(ultimo || 0) > 0 ? String(Number(ultimo) + 1) : '';
  return {
    numemp: siguiente,
    cedula: '',
    nombres: '',
    apellidos: '',
    direccion: '',
    sector: null,
    provincia: '',
    celular: '',
    telefono: '',
    email: '',
    sexo: 'Femenino',
    est_civil: 'Soltero',
    dependientes: '0',
    ocupacion: null,
    nacionalidad: 'Dominicano',
    tipodesangre: 'A-',
    cod_clase: 'Fijo',
    for_pago: 'Semanal',
    departamento: null,
    cod_ss: 'Si',
    aplicardescuento: 'Si',
    seguro: '',
    salario: '',
    fecha_ing: hoyISO(),
    fecha_nac: hoyISO(),
    status: 'Activo',
    fecha_liq: hoyISO(),
    cod_planta: '',
    cuentabanco: '',
  };
}

function opcionDe(lista, valor) {
  const texto = String(valor || '').trim();
  if (!texto) return null;
  return (
    (lista || []).find((o) => o.label === texto || String(o.id) === texto) || {
      id: texto,
      descrip: texto,
      label: texto,
    }
  );
}

function NoRowsOverlay() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: '100%', py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>Sin registros</Typography>
    </Stack>
  );
}

/**
 * Registro de empleados (fps2010).
 * Solo la fila `registroempleados` en roles: no hereda «nomina» ni AdmG.
 */
const RegistroEmpleados = () => {
  const { controller, selectedCompany, showSuccess, showError } = useProductosMaterialContext();
  const { usuarioLog, roleDetalles, nombreNegocio } = controller;
  const { post } = useApi();

  const puedeLeer = useMemo(
    () => puedeVerProgramaNominaPropio(roleDetalles, PROGRAMA_REGISTRO_EMPLEADOS),
    [roleDetalles]
  );
  const puedeModificar = puedeLeer;

  const [empleados, setEmpleados] = useState([]);
  const [ultimo, setUltimo] = useState(0);
  const [departamentos, setDepartamentos] = useState([]);
  const [sectores, setSectores] = useState([]);
  const [provincias, setProvincias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editando, setEditando] = useState(null);
  const [esNuevo, setEsNuevo] = useState(true);
  const [form, setForm] = useState(() => formularioVacio(0));
  const [pestana, setPestana] = useState(0);
  const [guardando, setGuardando] = useState(false);

  const puedeOperar = Boolean(selectedCompany?.db_empresa && usuarioLog);

  const credencialesEmpresa = useCallback(
    () => ({ id_empresa: selectedCompany }),
    [selectedCompany]
  );

  const setCampo = (campo) => (evento) => {
    const valor = evento?.target?.value ?? '';
    setForm((prev) => ({ ...prev, [campo]: valor }));
  };

  const cargar = useCallback(async () => {
    if (!puedeLeer || !puedeOperar) {
      setEmpleados([]);
      return;
    }
    setLoading(true);
    try {
      const [lista, cats] = await Promise.all([
        post('/api/nomina_listarregistroempleados', credencialesEmpresa()),
        post('/api/nomina_catalogosempleado', credencialesEmpresa()),
      ]);
      if (!lista.ok) throw new Error(lista.data.Error || 'Error al cargar empleados');
      if (!cats.ok) throw new Error(cats.data.Error || 'Error al cargar catálogos');
      setEmpleados(Array.isArray(lista.data.empleados) ? lista.data.empleados : []);
      setUltimo(Number(lista.data.ultimo || cats.data.ultimo || 0));
      setDepartamentos(Array.isArray(cats.data.departamentos) ? cats.data.departamentos : []);
      setSectores(Array.isArray(cats.data.sectores) ? cats.data.sectores : []);
      setProvincias(Array.isArray(cats.data.provincias) ? cats.data.provincias : []);
    } catch (e) {
      showError(e.message || 'Error al cargar el registro de empleados');
    } finally {
      setLoading(false);
    }
  }, [puedeLeer, puedeOperar, post, credencialesEmpresa, showError]);

  useEffect(() => {
    if (puedeLeer && puedeOperar) cargar();
  }, [puedeLeer, puedeOperar, cargar]);

  const cerrarDialogo = () => {
    if (guardando) return;
    setEditando(null);
    setPestana(0);
  };

  const abrirNuevo = () => {
    if (!puedeModificar) {
      showError('No tiene permisos para crear empleados');
      return;
    }
    setEsNuevo(true);
    setForm(formularioVacio(ultimo));
    setPestana(0);
    setEditando(0);
  };

  const abrirEditar = async (fila) => {
    if (!puedeModificar) {
      showError('No tiene permisos para modificar empleados');
      return;
    }
    try {
      const { ok, data } = await post('/api/nomina_obtenerempleado', {
        ...credencialesEmpresa(),
        numemp: fila.numemp,
      });
      if (!ok) throw new Error(data.Error || 'No se pudo leer el empleado');
      const emp = data.empleado || {};
      setEsNuevo(false);
      setForm({
        ...formularioVacio(ultimo),
        numemp: String(emp.numemp ?? ''),
        cedula: emp.cedula || '',
        nombres: emp.nombres || '',
        apellidos: emp.apellidos || '',
        direccion: emp.direccion || '',
        sector: opcionDe(sectores, emp.cod_sector),
        provincia: emp.provincia || '',
        celular: emp.celular || '',
        telefono: emp.telefono || '',
        email: emp.email || '',
        sexo: emp.sexo || 'Femenino',
        est_civil: emp.est_civil || 'Soltero',
        dependientes: String(emp.dependientes || '0'),
        ocupacion: opcionDe(departamentos, emp.cod_labor),
        nacionalidad: emp.nacionalidad || 'Dominicano',
        tipodesangre: emp.tipodesangre || 'A-',
        cod_clase: emp.cod_clase || 'Fijo',
        for_pago: emp.for_pago || 'Semanal',
        departamento: opcionDe(departamentos, emp.cod_depto),
        cod_ss: emp.cod_ss || 'Si',
        aplicardescuento: emp.aplicardescuento || 'Si',
        seguro: emp.seguro || '',
        salario: emp.salario || '',
        fecha_ing: emp.fecha_ing || hoyISO(),
        fecha_nac: emp.fecha_nac || hoyISO(),
        status: emp.status || 'Activo',
        fecha_liq: emp.fecha_liq || hoyISO(),
        cod_planta: emp.cod_planta || '',
        cuentabanco: emp.cuentabanco || '',
      });
      setPestana(0);
      setEditando(emp.numemp);
    } catch (e) {
      showError(e.message || 'No se pudo leer el empleado');
    }
  };

  const guardar = async () => {
    if (!puedeModificar) {
      showError('No tiene permisos para guardar empleados');
      return;
    }
    setGuardando(true);
    try {
      const { ok, data } = await post('/api/nomina_guardarempleado', {
        ...credencialesEmpresa(),
        nuevo: esNuevo ? 'S' : 'N',
        numemp: form.numemp,
        cedula: form.cedula,
        nombres: form.nombres,
        apellidos: form.apellidos,
        direccion: form.direccion,
        cod_sector: form.sector?.label || '',
        provincia: form.provincia,
        celular: form.celular,
        telefono: form.telefono,
        email: form.email,
        sexo: form.sexo,
        est_civil: form.est_civil,
        dependientes: form.dependientes,
        ocupacion: form.ocupacion?.label || '',
        nacionalidad: form.nacionalidad,
        tipodesangre: form.tipodesangre,
        clase: form.cod_clase,
        pago: form.for_pago,
        departamento: form.departamento?.label || '',
        seguridadsocial: form.cod_ss,
        descuentos: form.aplicardescuento,
        afiliacion: form.seguro,
        salario: form.salario,
        fecha_ing: form.fecha_ing,
        fecha_nac: form.fecha_nac,
        status: form.status,
        fechacancelacion: form.fecha_liq,
        planta: form.cod_planta,
        banco: form.cuentabanco,
      });
      if (!ok) throw new Error(data.Error || 'No se pueden grabar los datos');
      showSuccess(data.ok || 'Datos guardados');
      cerrarDialogo();
      await cargar();
    } catch (e) {
      showError(e.message || 'No se pueden grabar los datos');
    } finally {
      setGuardando(false);
    }
  };

  const columns = [
    { field: 'numemp', headerName: 'No.', width: 90 },
    { field: 'nombres', headerName: 'Nombres', flex: 1, minWidth: 140 },
    { field: 'apellidos', headerName: 'Apellidos', flex: 1, minWidth: 140 },
    { field: 'cedula', headerName: 'Cédula', width: 140 },
    { field: 'status', headerName: 'Estatus', width: 110 },
    { field: 'for_pago', headerName: 'Pago', width: 110 },
    { field: 'salario', headerName: 'Salario', width: 110 },
    {
      field: 'acciones',
      headerName: '',
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          size="small"
          startIcon={<EditOutlinedIcon />}
          onClick={() => abrirEditar(params.row)}
          disabled={!puedeModificar}
          sx={btnOutline(FP.color.navy)}
        >
          Editar
        </Button>
      ),
    },
  ];

  const renderAcceso = (mensaje) => (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
      <PageHeader
        title="Registro de Empleados"
        subtitle={nombreNegocio ? `${nombreNegocio} · Nómina` : 'Nómina'}
      />
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', justifyContent: 'center' }}>
        <Card
          elevation={0}
          sx={{ ...cardShellSx, maxWidth: 560, width: '100%', textAlign: 'center', p: { xs: 3, md: 4 } }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: 1.6,
              mx: 'auto',
              mb: 2.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: FP.color.dangerSoft,
              color: FP.color.danger,
            }}
          >
            <LockOutlinedIcon sx={{ fontSize: 30 }} />
          </Box>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: '1.25rem',
              color: FP.color.textPrimary,
              mb: 1,
              letterSpacing: '-0.01em',
            }}
          >
            Acceso denegado
          </Typography>
          <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.95rem', mb: 1.2 }}>
            {mensaje}
          </Typography>
          <Typography sx={{ color: FP.color.textMuted, fontSize: '0.85rem' }}>
            Por favor, contacte al administrador del sistema para solicitar los permisos necesarios.
          </Typography>
        </Card>
      </Box>
    </Box>
  );

  if (!puedeLeer) {
    return renderAcceso('Su rol no tiene acceso a esta opción (registroempleados).');
  }
  if (!puedeOperar) {
    return renderAcceso('Seleccione una empresa para administrar el registro de empleados.');
  }

  const campoSelect = (campo, label, opciones, required = true) => (
    <TextField
      select
      required={required}
      fullWidth
      label={label}
      value={form[campo]}
      onChange={setCampo(campo)}
      InputLabelProps={{ shrink: true }}
      sx={fpInputSx}
    >
      {opciones.map((op) => (
        <MenuItem key={op} value={op}>
          {op}
        </MenuItem>
      ))}
    </TextField>
  );

  const campoAuto = (campo, label, opciones) => (
    <Autocomplete
      options={opciones}
      value={form[campo]}
      onChange={(_, v) => setForm((p) => ({ ...p, [campo]: v }))}
      getOptionLabel={(o) => o?.label || ''}
      isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id) || a?.label === b?.label}
      renderInput={(params) => (
        <TextField
          {...params}
          required
          label={label}
          InputLabelProps={{ shrink: true }}
          sx={fpInputSx}
        />
      )}
    />
  );

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
      <PageHeader
        title="Registro de Empleados"
        subtitle={
          nombreNegocio
            ? `${nombreNegocio} · Último número ${ultimo || 0}`
            : `Último número ${ultimo || 0}`
        }
        cta={
          <Button
            startIcon={<AddIcon />}
            onClick={abrirNuevo}
            disabled={!puedeModificar}
            sx={btnOutline(FP.color.navy)}
          >
            Nuevo empleado
          </Button>
        }
      />
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={sectionBox}>
          <Grid container spacing={2} sx={{ mb: 1.5 }} alignItems="center">
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary, fontSize: '0.85rem' }}>
                {empleados.length} registros
              </Typography>
            </Grid>
          </Grid>
          <Box sx={{ bgcolor: '#fff' }}>
            <DataGrid
              autoHeight
              rows={empleados}
              columns={columns}
              loading={loading}
              getRowId={(r) => r.numemp}
              rowHeight={46}
              pageSizeOptions={[25, 50]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              slots={{ noRowsOverlay: NoRowsOverlay }}
              sx={{ ...fpDataGridSx, '--DataGrid-overlayHeight': '220px' }}
              disableRowSelectionOnClick
            />
          </Box>
        </Box>
      </Box>

      <Dialog
        open={editando !== null}
        onClose={cerrarDialogo}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 0 }}>
          {esNuevo ? 'Nuevo empleado' : `Editar empleado ${form.numemp}`}
        </DialogTitle>
        <DialogContent>
          <Tabs value={pestana} onChange={(_, v) => setPestana(v)} sx={{ ...tabSx, mt: 1, mb: 2 }}>
            <Tab label="Datos generales" />
            <Tab label="Datos nómina" />
          </Tabs>
          <Grid container spacing={2}>
            {pestana === 0 && (
              <>
                <Grid item xs={12} sm={4}>
                  <TextField
                    required
                    fullWidth
                    label="Número empleado"
                    value={form.numemp}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, numemp: e.target.value.replace(/\D/g, '').slice(0, 7) }))
                    }
                    disabled={!esNuevo}
                    inputProps={{ inputMode: 'numeric' }}
                    InputLabelProps={{ shrink: true }}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={8}>
                  <TextField
                    required
                    fullWidth
                    label="Cédula"
                    value={form.cedula}
                    onChange={setCampo('cedula')}
                    placeholder="000-0000000-0"
                    inputProps={{ maxLength: 13 }}
                    InputLabelProps={{ shrink: true }}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    label="Nombres"
                    value={form.nombres}
                    onChange={setCampo('nombres')}
                    inputProps={{ maxLength: 45 }}
                    InputLabelProps={{ shrink: true }}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    label="Apellidos"
                    value={form.apellidos}
                    onChange={setCampo('apellidos')}
                    inputProps={{ maxLength: 45 }}
                    InputLabelProps={{ shrink: true }}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    label="Dirección"
                    value={form.direccion}
                    onChange={setCampo('direccion')}
                    inputProps={{ maxLength: 100 }}
                    InputLabelProps={{ shrink: true }}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  {campoAuto('sector', 'Sector', sectores)}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={provincias}
                    value={form.provincia || null}
                    onChange={(_, v) => setForm((p) => ({ ...p, provincia: v || '' }))}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        label="Provincia"
                        InputLabelProps={{ shrink: true }}
                        sx={fpInputSx}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Celular"
                    value={form.celular}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, celular: e.target.value.replace(/\D/g, '').slice(0, 10) }))
                    }
                    placeholder="8090000000"
                    InputLabelProps={{ shrink: true }}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Teléfono"
                    value={form.telefono}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        telefono: e.target.value.replace(/\D/g, '').slice(0, 10),
                      }))
                    }
                    placeholder="8090000000"
                    InputLabelProps={{ shrink: true }}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email"
                    value={form.email}
                    onChange={setCampo('email')}
                    inputProps={{ maxLength: 100 }}
                    InputLabelProps={{ shrink: true }}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  {campoSelect('sexo', 'Sexo', SEXO)}
                </Grid>
                <Grid item xs={12} sm={4}>
                  {campoSelect('est_civil', 'Estado civil', ESTADO_CIVIL)}
                </Grid>
                <Grid item xs={12} sm={4}>
                  {campoSelect('dependientes', 'Dependientes', DEPENDIENTES)}
                </Grid>
                <Grid item xs={12} sm={4}>
                  {campoAuto('ocupacion', 'Ocupación', departamentos)}
                </Grid>
                <Grid item xs={12} sm={4}>
                  {campoSelect('nacionalidad', 'Nacionalidad', NACIONALIDAD)}
                </Grid>
                <Grid item xs={12} sm={4}>
                  {campoSelect('tipodesangre', 'Tipo de sangre', SANGRE)}
                </Grid>
              </>
            )}
            {pestana === 1 && (
              <>
                <Grid item xs={12} sm={4}>
                  {campoSelect('cod_clase', 'Clase', CLASE)}
                </Grid>
                <Grid item xs={12} sm={4}>
                  {campoSelect('for_pago', 'Pago', PAGO)}
                </Grid>
                <Grid item xs={12} sm={4}>
                  {campoAuto('departamento', 'Departamento', departamentos)}
                </Grid>
                <Grid item xs={12} sm={4}>
                  {campoSelect('cod_ss', 'Aplicar seguridad social', SI_NO)}
                </Grid>
                <Grid item xs={12} sm={4}>
                  {campoSelect('aplicardescuento', 'Descuentos', SI_NO)}
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="# Afiliación"
                    value={form.seguro}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, seguro: e.target.value.replace(/\D/g, '').slice(0, 13) }))
                    }
                    InputLabelProps={{ shrink: true }}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    required
                    fullWidth
                    label="Salario"
                    value={form.salario}
                    onChange={setCampo('salario')}
                    placeholder="0.00"
                    InputLabelProps={{ shrink: true }}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    required
                    fullWidth
                    type="date"
                    label="Fecha de ingreso"
                    value={form.fecha_ing}
                    onChange={setCampo('fecha_ing')}
                    InputLabelProps={{ shrink: true }}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    required
                    fullWidth
                    type="date"
                    label="Fecha de nacimiento"
                    value={form.fecha_nac}
                    onChange={setCampo('fecha_nac')}
                    InputLabelProps={{ shrink: true }}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  {campoSelect('status', 'Estatus', ESTATUS)}
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Fecha cancelación"
                    value={form.fecha_liq}
                    onChange={setCampo('fecha_liq')}
                    disabled={form.status === 'Activo'}
                    InputLabelProps={{ shrink: true }}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    required
                    fullWidth
                    label="Planta - Edificio"
                    value={form.cod_planta}
                    onChange={setCampo('cod_planta')}
                    inputProps={{ maxLength: 15 }}
                    InputLabelProps={{ shrink: true }}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    label="Banco / cuenta"
                    value={form.cuentabanco}
                    onChange={setCampo('cuentabanco')}
                    inputProps={{ maxLength: 50 }}
                    InputLabelProps={{ shrink: true }}
                    sx={fpInputSx}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={cerrarDialogo} disabled={guardando} sx={btnOutline(FP.color.textSecondary)}>
            Cancelar
          </Button>
          <Button
            onClick={guardar}
            disabled={guardando || !puedeModificar}
            startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : null}
            sx={btnSolid(FP.color.navy, FP.color.navyDeep)}
          >
            {guardando ? 'Guardando…' : esNuevo ? 'Guardar' : 'Modificar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RegistroEmpleados;
